// The replay bot's path: a recorded run (its commands, see replay.ts) played again with the game's
// own simulation, keeping where the player was each tick. The simulation is deterministic, so this
// is the exact run, and the server only has to hand out the small command replay (like a
// replay-bot plugin's frame file, but rebuilt here instead of stored).

import { createCmd } from '../sim/input';
import { EV_FINISHED, Simulation, type Level } from '../sim/simulation';
import { replayCmd, type Replay } from './replay';

/** Floats per tick: x, y, z (feet), yaw, ducked (0 or 1). */
export const GHOST_STRIDE = 5;
/** Ticks kept from before the timer starts (the run-up out of the start zone). */
const LEAD_IN = 50;
/**
 * Milliseconds of simulating before giving the page a moment. By time, not ticks: a tick on a big
 * loaded map costs far more than on the course, and the player is already playing (no hitches).
 */
const SLICE_MS = 2;

export interface GhostPath {
  /** Ticks in the path. */
  ticks: number;
  /** GHOST_STRIDE floats per tick. */
  data: Float32Array;
  /** The run's time on this level, in timer ticks (compared with the claimed time). */
  timerTicks: number;
}

/**
 * Play `r` on `level` (a level of its own, not the one being played) and keep the path from just
 * before the timer starts to the finish. Null if the run doesn't finish on this level, or if
 * `cancelled()` turns true (the player left the map) while it works.
 */
export async function ghostPath(
  level: Level,
  r: Replay,
  cancelled: () => boolean,
): Promise<GhostPath | null> {
  const sim = new Simulation(level);
  sim.cv.airaccelerate = r.airaccel;
  sim.autobhop = r.autobhop;
  sim.cv.slopeSlide = r.slopeSlide;
  sim.restart();
  sim.player.oldJump = r.jumpHeld;
  sim.events = 0;
  const cmd = createCmd();
  const all = new Float32Array(r.ticks * GHOST_STRIDE);
  let start = -1;
  let sliceEnd = performance.now() + SLICE_MS;
  for (let i = 0; i < r.ticks; i++) {
    if ((i & 31) === 0 && performance.now() > sliceEnd) {
      await new Promise((res) => setTimeout(res, 0));
      if (cancelled()) return null;
      sliceEnd = performance.now() + SLICE_MS;
    }
    sim.tick(replayCmd(r, i, cmd, sim.cv.forwardspeed));
    const p = sim.player;
    const o = i * GHOST_STRIDE;
    all[o] = p.pos[0];
    all[o + 1] = p.pos[1];
    all[o + 2] = p.pos[2];
    all[o + 3] = r.yaw[i];
    all[o + 4] = p.ducked ? 1 : 0;
    if (start < 0 && sim.timer.state === 'run') start = Math.max(0, i - LEAD_IN);
    if (sim.events & EV_FINISHED) {
      const from = Math.max(0, start);
      return {
        ticks: i + 1 - from,
        data: all.slice(from * GHOST_STRIDE, (i + 1) * GHOST_STRIDE),
        timerTicks: sim.timer.ticks,
      };
    }
    sim.events = 0;
  }
  return null;
}
